import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { MainLayout } from './components/layout/MainLayout'
import { LoginPage } from './pages/LoginPage'
import { EditProfilePage } from './pages/EditProfilePage'
import { ProtectedRoute } from './components/ProtectedRoute'
import { useAuth } from './contexts/AuthContext'
import { useConsumption, ConsumptionProvider } from './contexts/ConsumptionContext'
import { WaterHomePage } from './pages/consumption/WaterHomePage'
import { WaterReportPage } from './pages/consumption/WaterReportPage'
import { WaterGoalPage } from './pages/consumption/WaterGoalPage'
import { GasHomePage } from './pages/consumption/GasHomePage'
import { GasReportPage } from './pages/consumption/GasReportPage'
import { GasGoalPage } from './pages/consumption/GasGoalPage'
import { EnergyHomePage } from './pages/consumption/EnergyHomePage'

// Páginas públicas (não precisam de autenticação)
import { IosRedirectPage, PrivacyPage, AppLandingPage, FirstAccessPage, ForgotPasswordPage, ResetPasswordPage, RegistrationPage, AccountConfirmationPage, RegistrationApprovalPage } from './pages/public'

// Páginas admin
import { AdminCondominiumListPage, AdminResidenceUsersPage } from './pages/admin'

// Páginas de erro
import { Error403Page, Error404Page, Error500Page, Error503Page } from './pages/errors'

/**
 * Wrapper para página de Água - componente separado para evitar recriação
 */
function WaterHomeWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas, beginningOfPeriod } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={true}
      isWaterMenu={true}
      canAccessWater={canAccessWater}
      canAccessGas={canAccessGas || unit?.meterTypes?.includes('GAS')}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/agua/${residenceId}`}
      urlForReportMenu={`/consumo/agua/${residenceId}/relatorio`}
      urlForGoalMenu={`/consumo/agua/${residenceId}/meta`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      beginningOfPeriod={beginningOfPeriod}
    >
      <WaterHomePage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Relatório de Água - componente separado para evitar recriação
 */
function WaterReportWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={false}
      isReportMenu={true}
      isWaterMenu={true}
      canAccessWater={canAccessWater}
      canAccessGas={canAccessGas || unit?.meterTypes?.includes('GAS')}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/agua/${residenceId}`}
      urlForReportMenu={`/consumo/agua/${residenceId}/relatorio`}
      urlForGoalMenu={`/consumo/agua/${residenceId}/meta`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      breadCrumbSection="report"
    >
      <WaterReportPage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Meta de Água - componente separado para evitar recriação
 */
function WaterGoalWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={false}
      isGoalMenu={true}
      isWaterMenu={true}
      canAccessWater={canAccessWater}
      canAccessGas={canAccessGas || unit?.meterTypes?.includes('GAS')}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/agua/${residenceId}`}
      urlForReportMenu={`/consumo/agua/${residenceId}/relatorio`}
      urlForGoalMenu={`/consumo/agua/${residenceId}/meta`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      breadCrumbSection="goal"
    >
      <WaterGoalPage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Gás - componente separado para evitar recriação
 */
function GasHomeWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas, beginningOfPeriod } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={true}
      isGasMenu={true}
      isWaterMenu={false}
      canAccessWater={canAccessWater || unit?.meterTypes?.some((t: string) => t.startsWith('AGUA'))}
      canAccessGas={canAccessGas}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/gas/${residenceId}`}
      urlForReportMenu={`/consumo/gas/${residenceId}/relatorio`}
      urlForGoalMenu={`/consumo/gas/${residenceId}/meta`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      beginningOfPeriod={beginningOfPeriod}
    >
      <GasHomePage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Relatório de Gás - componente separado para evitar recriação
 */
function GasReportWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={false}
      isReportMenu={true}
      isGasMenu={true}
      isWaterMenu={false}
      canAccessWater={canAccessWater || unit?.meterTypes?.some((t: string) => t.startsWith('AGUA'))}
      canAccessGas={canAccessGas}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/gas/${residenceId}`}
      urlForReportMenu={`/consumo/gas/${residenceId}/relatorio`}
      urlForGoalMenu={`/consumo/gas/${residenceId}/meta`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      breadCrumbSection="report"
    >
      <GasReportPage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Meta de Gás - componente separado para evitar recriação
 */
function GasGoalWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={false}
      isGoalMenu={true}
      isGasMenu={true}
      isWaterMenu={false}
      canAccessWater={canAccessWater || unit?.meterTypes?.some((t: string) => t.startsWith('AGUA'))}
      canAccessGas={canAccessGas}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/gas/${residenceId}`}
      urlForReportMenu={`/consumo/gas/${residenceId}/relatorio`}
      urlForGoalMenu={`/consumo/gas/${residenceId}/meta`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      breadCrumbSection="goal"
    >
      <GasGoalPage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Energia - componente separado para evitar recriação
 */
function EnergyHomeWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas, canAccessEnergy, beginningOfPeriod } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;
  
  return (
    <MainLayout
      isHomeMenu={true}
      isEnergyMenu={true}
      isWaterMenu={false}
      isGasMenu={false}
      canAccessWater={canAccessWater || unit?.meterTypes?.some((t: string) => t.startsWith('AGUA'))}
      canAccessGas={canAccessGas || unit?.meterTypes?.includes('GAS')}
      canAccessEnergy={canAccessEnergy}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={`/consumo/energia/${residenceId}/consumo`}
      urlForReportMenu={`/consumo/energia/${residenceId}/demanda`}
      urlForGoalMenu={`/consumo/energia/${residenceId}/consumo`}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      beginningOfPeriod={beginningOfPeriod}
    >
      <EnergyHomePage />
    </MainLayout>
  );
}

/**
 * Wrapper para página de Edição de Perfil
 */
function EditProfileWithParams() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { currentResidenceId, currentUnit, canAccessWater, canAccessGas, canAccessEnergy } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  const units = user?.residences || [];
  const unit = units.find((u: { customerId: string }) => u.customerId === residenceId) || currentUnit;

  // Mantém o medidor selecionado (persistido pelo MainLayout) para não perder a
  // seleção/tema ao entrar no perfil, que não tem medidor na URL.
  const selectedMeter = localStorage.getItem('selected_meter') || 'agua';
  const isGas = selectedMeter === 'gas';
  const isEnergy = selectedMeter === 'energia';
  const meterBase = isEnergy
    ? `/consumo/energia/${residenceId}`
    : isGas
      ? `/consumo/gas/${residenceId}`
      : `/consumo/agua/${residenceId}`;
  const urlForHomeMenu = isEnergy ? `${meterBase}/consumo` : meterBase;
  const urlForReportMenu = isEnergy ? `${meterBase}/demanda` : `${meterBase}/relatorio`;
  const urlForGoalMenu = isEnergy ? `${meterBase}/consumo` : `${meterBase}/meta`;

  return (
    <MainLayout
      isHomeMenu={false}
      isWaterMenu={!isGas && !isEnergy}
      isGasMenu={isGas}
      isEnergyMenu={isEnergy}
      isAdjustMenu={true}
      canAccessWater={canAccessWater || unit?.meterTypes?.some((t: string) => t.startsWith('AGUA'))}
      canAccessGas={canAccessGas || unit?.meterTypes?.includes('GAS')}
      canAccessEnergy={canAccessEnergy || unit?.meterTypes?.includes('ENERGIA')}
      userFullName={user?.fullName || ''}
      profileUrl={user?.profileUrl}
      isApiUser={user?.isApiUser || false}
      units={units}
      currentResidenceId={residenceId}
      urlForHomeMenu={urlForHomeMenu}
      urlForReportMenu={urlForReportMenu}
      urlForGoalMenu={urlForGoalMenu}
      editUserRegistrationPath={`/condomino/${residenceId}/edit`}
      commercialServiceName={unit?.commercialServiceName || user?.residences?.[0]?.commercialServiceName || ''}
      residenceName={unit?.residenceName || user?.residences?.[0]?.residenceName || ''}
      breadCrumbSection="adjustments"
    >
      <EditProfilePage />
    </MainLayout>
  );
}

/**
 * App principal - SMR Customer UI
 * 
 * Todas as chamadas à API são reais, sem mocks.
 * O ConsumptionContext gerencia o estado da unidade selecionada.
 */
function AppRoutes() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { currentResidenceId } = useConsumption();

  // Aguarda carregamento da autenticação antes de decidir rotas
  if (isLoading) {
    return null; // ou um spinner de loading
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/primeiro-acesso" element={<FirstAccessPage />} />
        <Route path="/registrar" element={<RegistrationPage />} />
        <Route path="/confirmar-cadastro" element={<AccountConfirmationPage />} />
        <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
        <Route path="/senha/recuperar/:token" element={<ResetPasswordPage />} />
        <Route path="/cadastro/aprovar/:token" element={<RegistrationApprovalPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* ====== ROTAS PÚBLICAS (sem autenticação) ====== */}
      <Route path="/app" element={<AppLandingPage />} />
      <Route path="/app/privacy" element={<PrivacyPage />} />
      <Route path="/ios_app" element={<IosRedirectPage />} />
      <Route path="/primeiro-acesso" element={<FirstAccessPage />} />
      <Route path="/registrar" element={<RegistrationPage />} />
      <Route path="/confirmar-cadastro" element={<AccountConfirmationPage />} />
      <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
      <Route path="/senha/recuperar/:token" element={<ResetPasswordPage />} />
      <Route path="/cadastro/aprovar/:token" element={<RegistrationApprovalPage />} />

      {/* Rota de Login - página pública */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
      } />

      {/* ====== ROTAS PROTEGIDAS ====== */}
      
      {/* Rota principal - redireciona para água */}
      <Route path="/" element={
        <ProtectedRoute>
          <Navigate to={`/consumo/agua/${currentResidenceId}`} replace />
        </ProtectedRoute>
      } />

      {/* Rotas de consumo de água */}
      <Route path="/consumo/agua/:id" element={
        <ProtectedRoute>
          <WaterHomeWithParams />
        </ProtectedRoute>
      } />

      <Route path="/consumo/agua/:id/relatorio" element={
        <ProtectedRoute>
          <WaterReportWithParams />
        </ProtectedRoute>
      } />

      <Route path="/consumo/agua/:id/meta" element={
        <ProtectedRoute>
          <WaterGoalWithParams />
        </ProtectedRoute>
      } />

      {/* Rotas de consumo de gás */}
      <Route path="/consumo/gas/:id" element={
        <ProtectedRoute>
          <GasHomeWithParams />
        </ProtectedRoute>
      } />

      <Route path="/consumo/gas/:id/relatorio" element={
        <ProtectedRoute>
          <GasReportWithParams />
        </ProtectedRoute>
      } />

      <Route path="/consumo/gas/:id/meta" element={
        <ProtectedRoute>
          <GasGoalWithParams />
        </ProtectedRoute>
      } />

      {/* Rotas de consumo de energia */}
      <Route path="/consumo/energia/:id/consumo" element={
        <ProtectedRoute>
          <EnergyHomeWithParams />
        </ProtectedRoute>
      } />

      <Route path="/consumo/energia/:id/demanda" element={
        <ProtectedRoute>
          <EnergyHomeWithParams />
        </ProtectedRoute>
      } />

      {/* ====== ROTA DE EDIÇÃO DE PERFIL ====== */}
      <Route path="/condomino/:id/edit" element={
        <ProtectedRoute>
          <EditProfileWithParams />
        </ProtectedRoute>
      } />

      {/* ====== ROTAS ADMIN ====== */}
      <Route path="/admin/condominios" element={
        <ProtectedRoute>
          <AdminCondominiumListPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/condominio/:condominiumId" element={
        <ProtectedRoute>
          <AdminResidenceUsersPage />
        </ProtectedRoute>
      } />

      {/* ====== ROTAS DE ERRO ====== */}
      <Route path="/403" element={<Error403Page />} />
      <Route path="/500" element={<Error500Page />} />
      <Route path="/503" element={<Error503Page />} />

      {/* Fallback 404 - rota não encontrada */}
      <Route path="*" element={<Error404Page />} />
    </Routes>
  );
}

function App() {
  return (
    <ConsumptionProvider>
      <AppRoutes />
    </ConsumptionProvider>
  );
}

export default App
